// src/components/FloatingWhatsApp/FloatingWhatsApp.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";

import {
  t as translate,
  normalizeLang,
  getLangFromPathname,
} from "../../i18n";
import { useSiteContent } from "../../content/SiteContentContext";

const WHATSAPP_NUMBER = "358443519265";
const BUBBLE_STORAGE_KEY = "arttu_whatsapp_bubble_seen";

const copy = {
  fi: {
    message:
      "Hei Arttu! Olen kiinnostunut verkkosivuista ja haluaisin kysyä toteutuksesta. Voisitko olla yhteydessä?",
    bubble: "Voit olla yhteydessä myös WhatsAppilla",
    closeAria: "Sulje WhatsApp-vinkki",
    linkAria: "Lähetä WhatsApp-viesti",
    linkTitle: "Lähetä WhatsApp-viesti",
  },
  en: {
    message:
      "Hi Arttu! I’m interested in a website and would like to ask about a possible project. Could you get in touch with me?",
    bubble: "You can also contact me on WhatsApp",
    closeAria: "Close WhatsApp tip",
    linkAria: "Send a WhatsApp message",
    linkTitle: "Send a WhatsApp message",
  },
  es: {
    message:
      "¡Hola Arttu! Estoy interesado en una página web y me gustaría preguntarte sobre un posible proyecto. ¿Podrías ponerte en contacto conmigo?",
    bubble: "También puedes contactar conmigo por WhatsApp",
    closeAria: "Cerrar aviso de WhatsApp",
    linkAria: "Enviar un mensaje por WhatsApp",
    linkTitle: "Enviar un mensaje por WhatsApp",
  },
};

function buildWhatsAppUrl(number, message) {
  const cleanNumber = String(number || "").replace(/[^\d]/g, "");
  const encodedMessage = encodeURIComponent(message || "");

  return `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
}

export default function FloatingWhatsApp({ lang: langProp } = {}) {
  const location = useLocation();
  const siteContent = useSiteContent();

  const lang = useMemo(() => {
    if (typeof langProp === "string" && langProp) {
      return normalizeLang(langProp);
    }

    return getLangFromPathname(location?.pathname || "/");
  }, [langProp, location?.pathname]);

  // Lasketaan tekstit jokaisella renderöinnillä.
  // Näin WordPress-sisällön päivitys näkyy heti ilman turhaa useMemo-riippuvuutta.
  const fallback = copy[lang] || copy.fi;

  const pick = (key, fallbackText) => {
    const translationKey = `floatingWhatsApp.${key}`;
    const value = translate(translationKey, lang);

    return value === translationKey ? fallbackText : value;
  };

  const currentCopy = {
    message: pick("message", fallback.message),
    bubble: pick("bubble", fallback.bubble),
    closeAria: pick("closeAria", fallback.closeAria),
    linkAria: pick("linkAria", fallback.linkAria),
    linkTitle: pick("linkTitle", fallback.linkTitle),
  };

  const whatsappNumber =
    siteContent?.settings?.whatsappNumber || WHATSAPP_NUMBER;

  const [showBubble, setShowBubble] = useState(false);
  const [isNearFooter, setIsNearFooter] = useState(false);
  const [isIntroVisible, setIsIntroVisible] = useState(true);
  const [isContactVisible, setIsContactVisible] = useState(false);

  const href = useMemo(
    () => buildWhatsAppUrl(whatsappNumber, currentCopy.message),
    [whatsappNumber, currentCopy.message]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    // Intro ja footer ovat alueita, joissa kelluva WhatsApp ei saa peittää sisältöä.
    // Kuplan ajastus alkaa vasta, kun itse WhatsApp-painike saa näkyä.
    if (isIntroVisible || isContactVisible || isNearFooter) {
      setShowBubble(false);
      return undefined;
    }

    // Tallennetaan kuplan näyttö kielikohtaisesti.
    // Näin FI-kuplan näkeminen ei estä EN/ES-kuplaa samassa istunnossa.
    const storageKey = `${BUBBLE_STORAGE_KEY}_${lang}`;
    const hasSeenBubble = window.sessionStorage.getItem(storageKey);

    setShowBubble(false);

    if (hasSeenBubble) {
      return undefined;
    }

    const openTimer = window.setTimeout(() => {
      setShowBubble(true);
      window.sessionStorage.setItem(storageKey, "true");
    }, 1200);

    const closeTimer = window.setTimeout(() => {
      setShowBubble(false);
    }, 7200);

    return () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    };
  }, [lang, isIntroVisible, isContactVisible, isNearFooter]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateVisibility = () => {
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      // Hero / intro:
      // WhatsApp pysyy poissa koko ensimmäisen näkymän ajan.
      // Navbar on 64 px korkea, joten nappi ilmestyy vasta kun
      // intron alareuna on mennyt navbarin alapuolelta ohi.
      const intro = document.getElementById("intro");

      if (intro) {
        const introRect = intro.getBoundingClientRect();

        setIsIntroVisible(
          introRect.bottom > 64 && introRect.top < viewportHeight
        );
      } else {
        setIsIntroVisible(false);
      }

      // Contact:
      // Lomakkeessa WhatsApp ei saa kellua kenttien tai lähetyspainikkeen päällä.
      const contact = document.getElementById("contact");

      if (contact) {
        const contactRect = contact.getBoundingClientRect();

        setIsContactVisible(
          contactRect.bottom > 0 && contactRect.top < viewportHeight
        );
      } else {
        setIsContactVisible(false);
      }

      // Footer:
      // Piilotetaan nappi footerin lähestyessä.
      const footer = document.querySelector("footer");

      if (!footer) {
        setIsNearFooter(false);
        return;
      }

      const footerTop = footer.getBoundingClientRect().top;

      setIsNearFooter(footerTop < viewportHeight - 40);
    };

    updateVisibility();

    window.addEventListener("scroll", updateVisibility, {
      passive: true,
    });
    window.addEventListener("resize", updateVisibility);

    return () => {
      window.removeEventListener("scroll", updateVisibility);
      window.removeEventListener("resize", updateVisibility);
    };
  }, [location.pathname]);

  const handleClick = () => {
    setShowBubble(false);

    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "whatsapp_click", {
        sijainti: "floating_whatsapp",
        kanava: "whatsapp",
        lang,
      });
    }
  };

  if (isIntroVisible || isContactVisible || isNearFooter) {
    return null;
  }

  return (
    <div className="fixed bottom-[5.25rem] right-5 z-[9999] flex flex-col items-end gap-3 md:bottom-5">
      {showBubble && (
        <div
          className="
            relative max-w-[250px] rounded-2xl
            border border-black/10 bg-white px-4 py-3 pr-9
            text-sm font-semibold leading-snug text-gray-900
            shadow-[0_16px_40px_rgba(0,0,0,0.18)]
            dark:border-white/10 dark:bg-slate-900 dark:text-white
          "
        >
          <button
            type="button"
            aria-label={currentCopy.closeAria}
            onClick={() => setShowBubble(false)}
            className="
              absolute right-2 top-2
              inline-flex h-5 w-5 items-center justify-center
              rounded-full text-xs text-gray-500
              hover:bg-black/5 hover:text-gray-900
              dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white
            "
          >
            ×
          </button>

          {currentCopy.bubble}

          <span
            aria-hidden="true"
            className="
              absolute -bottom-2 right-6 h-4 w-4 rotate-45
              border-b border-r border-black/10 bg-white
              dark:border-white/10 dark:bg-slate-900
            "
          />
        </div>
      )}

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        aria-label={currentCopy.linkAria}
        title={currentCopy.linkTitle}
        className="
          inline-flex h-12 w-12 items-center justify-center
          rounded-full bg-[#25D366] text-white
          shadow-[0_12px_30px_rgba(37,211,102,0.35)]
          ring-1 ring-white/30
          transition-transform duration-200
          hover:scale-105 active:scale-95
          focus:outline-none focus:ring-4 focus:ring-[#25D366]/35
          md:h-14 md:w-14
        "
      >
        <FaWhatsapp className="h-7 w-7 md:h-8 md:w-8" aria-hidden="true" />
        <span className="sr-only">WhatsApp</span>
      </a>
    </div>
  );
}